import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDocumentsByIds } from "@/stores/documents/repository/queries";
import type { DocumentWithTags } from "@/domains/documents/components/organisms/DocumentListTable";
import { toDocumentWithTags } from "@/domains/documents/utils/documentWithTags";
import { briefKeys } from "../queryKeys";
import {
  fetchBriefChecks,
  fetchBriefFindings,
  fetchBriefMembers,
  fetchBriefVersionDeletions,
  fetchBriefVersions,
  fetchHiddenDocumentCount,
  fetchBriefAppendices,
  fetchUnconfirmedChanges,
} from "../repository";
import { bySeverityThenNewest } from "../findingPresentation";
import type {
  BriefAppendix,
  BriefCheckRequest,
  BriefFinding,
  BriefMember,
  BriefVersionDeletion,
} from "../types";

const NO_APPENDICES: BriefAppendix[] = [];

/**
 * Every brief cache keeps its raw repository shape; consumers derive in
 * render (CLAUDE.md: a shared queryKey is a shared cache entry, so no
 * consumer may reshape it inside its queryFn).
 */

export interface BriefDocumentsData {
  members: BriefMember[];
  /** The members as list-table rows (open, cite and name them), in member order. */
  rows: DocumentWithTags[];
  /** Members the caller's folder access hides from them. */
  hiddenCount: number;
}

/**
 * The live set — every document in the Brief folder's subtree — as members and
 * as `DocumentWithTags` rows. The Brief tab itself renders the folder view; the
 * page reads this for citations, the viewer, counts and the live payload.
 */
export function useBriefDocuments(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.documents(briefId),
    queryFn: async (): Promise<BriefDocumentsData> => {
      const [members, hiddenCount] = await Promise.all([
        fetchBriefMembers(briefId as number),
        fetchHiddenDocumentCount(briefId as number),
      ]);
      // `fetchDocumentsByIds` carries version, verified and the tag embeds —
      // the same select the bulk actions resolve their selection through.
      const docs = await fetchDocumentsByIds(
        members.map((m) => m.document_id),
        { throwOnError: true }
      );
      const byId = new Map(docs.map((d) => [d.id, d]));
      const rows: DocumentWithTags[] = [];
      for (const m of members) {
        const doc = byId.get(m.document_id);
        if (doc) rows.push(toDocumentWithTags(doc));
      }
      return { members, rows, hiddenCount };
    },
    enabled: enabled && briefId !== null,
  });

  const members = useMemo(() => query.data?.members ?? [], [query.data]);
  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);

  return {
    members,
    rows,
    hiddenCount: query.data?.hiddenCount ?? 0,
    error: query.error,
  };
}

/** Franklin's suggestions: open ones worst first, settled ones most recent first. */
export function useBriefFindings(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.findings(briefId),
    queryFn: () => fetchBriefFindings(briefId as number),
    enabled: enabled && briefId !== null,
  });
  const findings = useMemo(() => query.data ?? [], [query.data]);
  const suggestions = useMemo(
    () => findings.filter((f) => f.status === "open").sort(bySeverityThenNewest),
    [findings]
  );
  const settled = useMemo(
    () =>
      findings
        .filter((f) => f.status !== "open")
        .sort((a, b) => (b.resolved_at ?? "").localeCompare(a.resolved_at ?? "")),
    [findings]
  );
  return { findings, suggestions, settled, error: query.error };
}

/** The brief's appendices, oldest first, with their threads. */
export function useBriefAppendices(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.appendices(briefId),
    queryFn: () => fetchBriefAppendices(briefId as number),
    enabled: enabled && briefId !== null,
  });
  return {
    appendices: query.data ?? NO_APPENDICES,
    error: query.error,
  };
}

export function useBriefVersions(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.versions(briefId),
    queryFn: () => fetchBriefVersions(briefId as number),
    enabled: enabled && briefId !== null,
  });
  const versions = useMemo(() => query.data ?? [], [query.data]);
  return {
    versions,
    hasData: query.data !== undefined,
    isLoading: query.isPending,
    error: query.error,
  };
}

const NO_DELETIONS: BriefVersionDeletion[] = [];

/**
 * Deleted versions, newest deletion first: the history, and the numbers the
 * next confirm must not reuse. `available` is false before the pipeline
 * migration has applied (no Delete then) and undefined while unknown.
 */
export function useBriefVersionDeletions(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.versionDeletions(briefId),
    queryFn: () => fetchBriefVersionDeletions(briefId as number),
    enabled: enabled && briefId !== null,
  });
  return {
    deletions: query.data ?? NO_DELETIONS,
    available: query.data === undefined ? undefined : query.data !== null,
    isLoading: query.isPending,
    error: query.error,
  };
}

export function useBriefUnconfirmed(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.unconfirmed(briefId),
    queryFn: () => fetchUnconfirmedChanges(briefId as number),
    enabled: enabled && briefId !== null,
    // Always re-read on arrival: the page takes what is unconfirmed when the
    // visit starts as its baseline, and a cached answer from an earlier visit
    // would make changes made since read as this visit's.
    refetchOnMount: "always",
  });
  return {
    unconfirmed: query.data ?? null,
    /** The data is this visit's own read, not a cached one. */
    fetchedThisVisit: query.isFetchedAfterMount,
  };
}

const ACTIVE: ReadonlySet<BriefCheckRequest["status"]> = new Set(["pending", "running"]);
const SETTLED: ReadonlySet<BriefCheckRequest["status"]> = new Set([
  "completed",
  "failed",
  "skipped",
]);

/** Newest runs → what the status pill shows: the one in flight, and the last that settled. */
export function useBriefCheckStatus(briefId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: briefKeys.checks(briefId),
    queryFn: () => fetchBriefChecks(briefId as number),
    enabled: enabled && briefId !== null,
    // A pending row's not_before passing does not produce an event; the poll
    // is only a fallback for the realtime subscription.
    refetchInterval: 60_000,
  });
  const checks = useMemo(() => query.data ?? [], [query.data]);
  const active = useMemo(() => checks.find((c) => ACTIVE.has(c.status)) ?? null, [checks]);
  const lastRun = useMemo(() => checks.find((c) => SETTLED.has(c.status)) ?? null, [checks]);
  return { active, lastRun };
}
